/**
 * JSON import/export.
 *
 * Export: serialize the full persistable app state to JSON, trigger a download.
 * Import: parse a JSON file, validate it loosely (we trust it more than seed.json
 * because it came from this same app), and replace LocalStorage.
 *
 * Format: identical to LocalStorage shape (encoded via encodeForStorage), wrapped
 * with version + exportedAt for forward compatibility.
 */

import { encodeForStorage, decodeFromStorage } from '../../schemas/persistence.js';

export interface ExportPayload {
  version: 1;
  exportedAt: string;
  app: 'btc-stack-planner';
  /** Encoded state matching the LocalStorage shape. */
  state: unknown;
}

export function buildExportBlob(state: object): Blob {
  const payload: ExportPayload = {
    version: 1,
    exportedAt: new Date().toISOString(),
    app: 'btc-stack-planner',
    state: encodeForStorage(state),
  };
  return new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
}

export function downloadExport(state: object): void {
  const blob = buildExportBlob(state);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const stamp = new Date().toISOString().slice(0, 10);
  a.download = `btc-stack-planner-${stamp}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export interface ParsedImport {
  state: Record<string, unknown>;
  exportedAt: string;
}

export function parseImport(json: string): ParsedImport {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch (e) {
    throw new Error(`Invalid JSON: ${e instanceof Error ? e.message : String(e)}`);
  }
  if (!parsed || typeof parsed !== 'object') {
    throw new Error('Expected a JSON object');
  }
  const obj = parsed as { app?: string; version?: number; state?: unknown; exportedAt?: string };
  if (obj.app !== 'btc-stack-planner') {
    throw new Error(`Not a btc-stack-planner export (app="${obj.app ?? 'undefined'}")`);
  }
  if (obj.version !== 1) {
    throw new Error(`Unsupported export version ${obj.version}, expected 1`);
  }
  if (!obj.state || typeof obj.state !== 'object') {
    throw new Error('Export is missing the "state" field');
  }
  const decoded = decodeFromStorage(obj.state);
  if (!decoded || typeof decoded !== 'object') {
    throw new Error('Could not decode state field');
  }
  return {
    state: decoded as Record<string, unknown>,
    exportedAt: obj.exportedAt ?? '',
  };
}
